import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { reportPotCollectionReelReady } from "../../State/actions";
import { selectPotCollectionSymbolsReady } from "../../State/selectors";
import PotCollectionSymbol from "./PotCollectionSymbol";
import { isBonusSymbol, BLANK_SYMBOL_ID } from "../../SymbolData";

/**
 * PotCollectionReel
 *
 * Column container for Pot Collection symbols.
 * Creates SYMBOLS_VISIBLE symbol components per reel.
 * Reports readiness to Redux when all symbols are populated.
 * Mirrors RoyalCollectReel pattern (no spin controller).
 */
const PotCollectionReel = forwardRef(
  (
    {
      reel,
      index,
      x,
      symbolWidth,
      symbolHeight,
      symbolsSpriteSheet,
    },
    ref,
  ) => {
    const symbolRefs = useRef([]);
    const reportedReadyRef = useRef(false);
    const dispatch = useDispatch();
    const potCollectionSymbolsReady = useSelector(
      selectPotCollectionSymbolsReady,
    );

    // Initialize symbol refs array
    useEffect(() => {
      if (symbolRefs.current.length !== reel.length) {
        symbolRefs.current = Array.from({ length: reel.length }, () =>
          React.createRef(),
        );
      }
    }, [reel.length]);

    const getSymbolRef = useCallback((symbolIndex) => {
      return symbolRefs.current[symbolIndex];
    }, []);

    const getSymbolRefs = useCallback(() => {
      return symbolRefs.current;
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getSymbolRef,
        getSymbolRefs,
      }),
      [getSymbolRef, getSymbolRefs],
    );

    // Report to Redux when this reel's refs are ready
    useEffect(() => {
      if (!potCollectionSymbolsReady || reportedReadyRef.current) {
        return;
      }

      const allRefsPopulated =
        symbolRefs.current.length === reel.length &&
        symbolRefs.current.every((r) => r && r.current);

      if (allRefsPopulated) {
        dispatch(reportPotCollectionReelReady());
        reportedReadyRef.current = true;
      }
    }, [potCollectionSymbolsReady, reel.length, index, dispatch]);

    return (
      <Container name={`pc_reel_${index}`} x={x}>
        {reel.map((symbol, arrayIdx) => {
          // Zeus orbs are spine-only (overlay handles all visuals) — no texture at all.
          // All other positions start as blank thunderbolts; coins eject onto them later.
          const displaySymbolId = isBonusSymbol(symbol) ? null : BLANK_SYMBOL_ID;

          return (
            <PotCollectionSymbol
              key={`pc_sym_${arrayIdx}`}
              ref={symbolRefs.current[arrayIdx]}
              index={arrayIdx}
              reelIndex={index}
              symbolWidth={symbolWidth}
              symbolHeight={symbolHeight}
              symbolId={displaySymbolId}
              symbolsSpriteSheet={symbolsSpriteSheet}
            />
          );
        })}
      </Container>
    );
  },
);

PotCollectionReel.displayName = "PotCollectionReel";

export default PotCollectionReel;
